import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { redirectToSso } from "../auth/redirectToSso";

const env = (import.meta as unknown as Record<string, Record<string, string>>).env;
const baseURL = env?.VITE_APP_BACKEND_URL ?? "http://localhost:8000/erp";
const ssoApiUrl = env?.VITE_APP_SSO_API_URL ?? "http://localhost:8000/sso";
const clientId = env?.VITE_APP_SSO_CLIENT_ID ?? "";

export const httpClient = axios.create({ baseURL });

function getAccessToken(): string | null {
  return localStorage.getItem("access_token");
}

function getRefreshToken(): string | null {
  return localStorage.getItem("refresh_token");
}

function setTokens(accessToken: string, refreshToken?: string): void {
  localStorage.setItem("access_token", accessToken);
  if (refreshToken) {
    localStorage.setItem("refresh_token", refreshToken);
  }
}

function clearTokens(): void {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("id_token");
}

// Flag & Queue untuk menangani multiple request concurrent saat token expired
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

function processQueue(error: any, token: string | null = null): void {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });
  failedQueue = [];
}

httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token) {
      config.headers.set ? config.headers.set("Authorization", `Bearer ${token}`) : (config.headers.Authorization = `Bearer ${token}`);
    }
    return config;
  },
  (error) => Promise.reject(error),
);

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Jika request sendiri sudah ke endpoint token, langsung logout & redirect
      if (originalRequest.url?.includes("/oauth2/token")) {
        clearTokens();
        redirectToSso(window.location.pathname + window.location.search);
        return Promise.reject(error);
      }

      // Jika proses refresh sedang berjalan, masukkan request ke antrean
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.set ? originalRequest.headers.set("Authorization", `Bearer ${token}`) : ((originalRequest.headers as any).Authorization = `Bearer ${token}`);
            return httpClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearTokens();
        redirectToSso(window.location.pathname + window.location.search);
        return Promise.reject(error);
      }

      try {
        // Gunakan axios standar (bukan httpClient) agar request refresh tidak masuk interceptor ini lagi
        const response = await axios.post<{
          data: {
            access_token?: string;
            accessToken?: string;
            refresh_token?: string;
            refreshToken?: string;
          };
        }>(`${ssoApiUrl}/oauth2/token`, {
          grantType: "refresh_token",
          refreshToken,
          clientId,
        });

        const newAccessToken = response.data.data.access_token || response.data.data.accessToken;
        const newRefreshToken = response.data.data.refresh_token || response.data.data.refreshToken;

        if (!newAccessToken) {
          throw new Error("Gagal memperbarui token.");
        }

        setTokens(newAccessToken, newRefreshToken);

        originalRequest.headers.set ? originalRequest.headers.set("Authorization", `Bearer ${newAccessToken}`) : ((originalRequest.headers as any).Authorization = `Bearer ${newAccessToken}`);

        processQueue(null, newAccessToken);
        return httpClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        redirectToSso(window.location.pathname + window.location.search);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
